<template>
  <div class="dashboard-page" v-loading="loading">
    <section class="topbar-panel">
      <div class="topbar-main">
        <div class="title-block">
          <span class="title-tag">运营总览</span>
          <h1>积分商城数据驾驶舱</h1>
          <p>围绕会员、订单、积分和库存，快速判断今天该补货、该催单还是该排查异常。</p>
        </div>

        <div class="filter-block">
          <div class="block-label">时间筛选</div>
          <div class="range-list">
            <button
              v-for="item in rangeOptions"
              :key="item.key"
              class="range-button"
              :class="{ active: activeRange === item.key }"
              @click="changeRange(item.key)"
            >
              {{ item.label }}
            </button>
          </div>
          <el-date-picker
            v-if="activeRange === 'custom'"
            v-model="customRange"
            type="daterange"
            unlink-panels
            value-format="yyyy-MM-dd"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            range-separator="至"
            class="custom-picker"
            @change="handleCustomRangeChange"
          />
          <div class="range-caption">当前口径：{{ currentRangeLabel }}</div>
        </div>

        <div class="quick-block">
          <div class="block-label">快捷操作</div>
          <div class="quick-grid">
            <button
              v-for="item in quickActions"
              :key="item.key"
              class="quick-action-button"
              :class="{ primary: item.primary }"
              @click="handleQuickAction(item.key)"
            >
              {{ item.label }}
            </button>
          </div>
        </div>
      </div>

      <div class="topbar-side">
        <div class="manager-card">
          <div class="manager-title">当前管理员</div>
          <strong>{{ currentAdminName }}</strong>
          <span>最近刷新：{{ latestUpdateText }}</span>
        </div>

        <div class="notice-card">
          <div class="notice-head">
            <span>消息与告警</span>
            <em>{{ formatNumber(alertCount) }}</em>
          </div>
          <div class="notice-line">
            <span>库存 / 任务 / 售后风险</span>
            <strong>{{ alertHeadline }}</strong>
          </div>
          <div class="notice-actions">
            <el-button size="mini" type="text" @click="scrollToSection('warning-panel')">查看告警</el-button>
            <el-button size="mini" type="text" @click="goRoute('/system/operation-log')">系统日志</el-button>
            <el-button size="mini" type="text" @click="loadDashboard">刷新驾驶舱</el-button>
          </div>
        </div>
      </div>
    </section>

    <section class="kpi-row">
      <article v-for="item in kpiCards" :key="item.label" class="kpi-card" :class="item.tone">
        <div class="kpi-header">
          <span>{{ item.label }}</span>
          <small>{{ item.badge }}</small>
        </div>
        <div class="kpi-value">{{ item.value }}</div>
        <div class="kpi-extra">{{ item.extra }}</div>
      </article>
    </section>

    <section class="grid-row">
      <article class="panel-card">
        <div class="panel-head">
          <div>
            <h3>{{ orderTrendTitle }}</h3>
            <p>观察兑换节奏，快速判断活动拉动和履约压力。</p>
          </div>
          <strong>{{ formatNumber(orderTrendTotal) }} 单</strong>
        </div>
        <div class="chart-shell">
          <svg class="trend-svg" viewBox="0 0 520 220" preserveAspectRatio="none">
            <defs>
              <linearGradient id="orderArea" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stop-color="#51a8ff" stop-opacity="0.35" />
                <stop offset="100%" stop-color="#51a8ff" stop-opacity="0.04" />
              </linearGradient>
            </defs>
            <path v-if="orderChart.areaPath" :d="orderChart.areaPath" fill="url(#orderArea)" />
            <path
              v-if="orderChart.linePath"
              :d="orderChart.linePath"
              fill="none"
              stroke="#51a8ff"
              stroke-width="3"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <circle
              v-for="point in orderChart.points"
              :key="point.key"
              :cx="point.x"
              :cy="point.y"
              r="4"
              fill="#ffffff"
              stroke="#51a8ff"
              stroke-width="2"
            />
          </svg>
          <div class="chart-axis">
            <span v-for="tick in orderChart.ticks" :key="tick.key">{{ tick.label }}</span>
          </div>
        </div>
        <div class="panel-foot">
          <span>峰值：{{ formatNumber(orderChart.maxValue) }} 单</span>
          <span>平均：{{ formatNumber(orderTrendAverage) }} 单 / 天</span>
          <span>待推进：{{ formatNumber(summary.pendingOrders) }} 单</span>
        </div>
      </article>

      <article class="panel-card">
        <div class="panel-head">
          <div>
            <h3>{{ pointTrendTitle }}</h3>
            <p>重点追踪积分消耗和后台扣减波动，防止异常消耗放大。</p>
          </div>
          <strong>{{ formatNumber(pointTrendTotal) }} 分</strong>
        </div>
        <div class="chart-shell">
          <svg class="trend-svg" viewBox="0 0 520 220" preserveAspectRatio="none">
            <defs>
              <linearGradient id="pointArea" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stop-color="#ff8a4c" stop-opacity="0.35" />
                <stop offset="100%" stop-color="#ff8a4c" stop-opacity="0.04" />
              </linearGradient>
            </defs>
            <path v-if="pointChart.areaPath" :d="pointChart.areaPath" fill="url(#pointArea)" />
            <path
              v-if="pointChart.linePath"
              :d="pointChart.linePath"
              fill="none"
              stroke="#ff8a4c"
              stroke-width="3"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <circle
              v-for="point in pointChart.points"
              :key="point.key"
              :cx="point.x"
              :cy="point.y"
              r="4"
              fill="#ffffff"
              stroke="#ff8a4c"
              stroke-width="2"
            />
          </svg>
          <div class="chart-axis">
            <span v-for="tick in pointChart.ticks" :key="tick.key">{{ tick.label }}</span>
          </div>
        </div>
        <div class="panel-foot">
          <span>峰值：{{ formatNumber(pointChart.maxValue) }} 分</span>
          <span>平均：{{ formatNumber(pointTrendAverage) }} 分 / 天</span>
          <span>净变化：{{ signedNumber(summary.netPoints) }}</span>
        </div>
      </article>
    </section>

    <section class="grid-row">
      <article class="panel-card">
        <div class="panel-head">
          <div>
            <h3>商品品类兑换分析</h3>
            <p>用商品销量映射品类热度，帮助你优先安排补货和推荐位。</p>
          </div>
          <strong>{{ categorySlices.length }} 类</strong>
        </div>
        <div class="analysis-content category-content">
          <div class="donut-wrap">
            <div class="donut-chart" :style="categoryChartStyle">
              <div class="donut-core">
                <span>热度最高</span>
                <strong>{{ topCategoryName }}</strong>
                <small>{{ topCategoryShare }}</small>
              </div>
            </div>
          </div>
          <div class="legend-list">
            <div v-for="item in categorySlices" :key="item.name" class="legend-item">
              <span class="legend-dot" :style="{ background: item.color }" />
              <div class="legend-main">
                <strong>{{ item.name }}</strong>
                <small>{{ formatNumber(item.value) }} 次兑换</small>
              </div>
              <em>{{ item.percent }}%</em>
            </div>
            <div v-if="!categorySlices.length" class="empty-state">暂无商品兑换分类数据</div>
          </div>
        </div>
      </article>

      <article class="panel-card">
        <div class="panel-head">
          <div>
            <h3>会员等级兑换状态</h3>
            <p>观察等级结构和签到积分投放，判断会员运营是否需要提档。</p>
          </div>
          <strong>{{ formatNumber(summary.totalMembers) }} 人</strong>
        </div>
        <div class="level-list">
          <div v-for="item in visibleMemberLevelCards" :key="item.levelName" class="level-item">
            <div class="level-top">
              <strong>{{ item.levelName }}</strong>
              <span>{{ formatNumber(item.count) }} 人</span>
            </div>
            <div class="level-bar">
              <span :style="{ width: item.percent + '%', background: item.color }" />
            </div>
            <div class="level-meta">
              <span>占比 {{ item.percent }}%</span>
              <span>签到 {{ formatNumber(item.signInPoints) }} 分</span>
              <span>{{ formatDiscount(item.discountRate) }}</span>
            </div>
          </div>
          <div v-if="!memberLevelCards.length" class="empty-state">暂无会员等级分布数据</div>
          <button
            v-if="hiddenMemberLevelCount > 0"
            class="level-toggle"
            @click="memberLevelExpanded = !memberLevelExpanded"
          >
            <span>{{ memberLevelExpanded ? '收起会员等级' : `展开全部 ${memberLevelCards.length} 个等级` }}</span>
            <i :class="{ expanded: memberLevelExpanded }">⌄</i>
          </button>
        </div>
      </article>
    </section>

    <section class="grid-row">
      <article class="panel-card">
        <div class="panel-head">
          <div>
            <h3>实时兑换动态日志</h3>
            <p>自动滚动查看最新兑换和售后单，鼠标移入可暂停。</p>
          </div>
          <strong>{{ formatNumber(activityLogs.length) }} 条</strong>
        </div>
        <div
          class="activity-marquee"
          @mouseenter="pauseAutoScroll"
          @mouseleave="resumeAutoScroll"
        >
          <div class="activity-track" :style="activityTrackStyle">
            <div v-for="item in scrollingActivityLogs" :key="item.key" class="activity-item">
              <div class="activity-time">{{ item.time }}</div>
              <div class="activity-content">
                <div class="activity-title">
                  <strong>{{ item.title }}</strong>
                  <span :class="['status-chip', item.statusClass]">{{ item.statusText }}</span>
                </div>
                <div class="activity-desc">{{ item.desc }}</div>
              </div>
              <div class="activity-side">{{ item.side }}</div>
            </div>
          </div>
          <div v-if="!activityLogs.length" class="empty-state activity-empty">当前筛选范围内暂无兑换动态</div>
        </div>
      </article>

      <article id="warning-panel" class="panel-card">
        <div class="panel-head">
          <div>
            <h3>系统异常告警面板</h3>
            <p>将库存风险、停用任务、售后压力和失败日志收拢到一处处理。</p>
          </div>
          <strong>{{ formatNumber(alertCount) }} 项</strong>
        </div>
        <div
          class="warning-marquee"
          @mouseenter="pauseWarningAutoScroll"
          @mouseleave="resumeWarningAutoScroll"
        >
          <div class="warning-list" :style="warningTrackStyle">
            <div v-for="item in scrollingWarningCards" :key="item.key" class="warning-item" :class="item.level">
              <div class="warning-main">
                <div class="warning-title">
                  <strong>{{ item.title }}</strong>
                  <span>{{ item.tag }}</span>
                </div>
                <p>{{ item.desc }}</p>
              </div>
              <el-button size="mini" round @click="handleWarningAction(item.action)">{{ item.actionLabel }}</el-button>
            </div>
            <div v-if="!warningCards.length" class="empty-state">当前没有需要立即处理的系统告警，运行状态良好。</div>
          </div>
        </div>
      </article>
    </section>
  </div>
</template>

<script>
import http from '../../utils/http'

const RANGE_OPTIONS = [
  { key: 'today', label: '今日' },
  { key: 'yesterday', label: '昨日' },
  { key: '7d', label: '7天' },
  { key: '30d', label: '30天' },
  { key: 'month', label: '本月' },
  { key: 'custom', label: '自定义' }
]

const CATEGORY_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#f97316', '#8b5cf6', '#14b8a6']
const LEVEL_COLORS = ['#5b8ff9', '#61d9a4', '#f6bd16', '#ff8a4c', '#9270ca', '#78d3f8']
const ACTIVITY_ROW_HEIGHT = 102
const WARNING_ROW_HEIGHT = 116
const ROUTE_PERMISSION_MAP = {
  '/mall/goods': 'mall:goods:list',
  '/mall/order': 'mall:order:list',
  '/mall/member-level': 'mall:member-level:list',
  '/mall/point-task': 'mall:point-task:list',
  '/system/operation-log': 'sys:operation-log:list'
}

function buildEmptyDashboard () {
  return {
    summary: buildEmptySummary(),
    orderTrend: buildEmptyTrend(),
    pointTrend: buildEmptyTrend(),
    categorySlices: [],
    memberLevelCards: [],
    activityLogs: [],
    warningCards: [],
    currentAdminName: '系统管理员',
    latestUpdateTime: null,
    rangeLabel: '',
    rangeShortLabel: '',
    trendDays: 7
  }
}

function buildEmptySummary () {
  return {
    totalMembers: 0,
    activeMembers: 0,
    totalOrders: 0,
    pendingOrders: 0,
    completedOrders: 0,
    refundOrders: 0,
    totalConsumedPoints: 0,
    totalGrantedPoints: 0,
    expiredPoints: 0,
    netPoints: 0,
    lowStockGoods: 0
  }
}

function buildEmptyTrend () {
  return {
    days: 7,
    total: 0,
    average: 0,
    maxValue: 0,
    series: []
  }
}

export default {
  data () {
    return {
      loading: false,
      activeRange: '30d',
      customRange: [],
      dashboard: buildEmptyDashboard(),
      activityIndex: 0,
      activityTransitionEnabled: true,
      activityTimer: null,
      warningIndex: 0,
      warningTransitionEnabled: true,
      warningTimer: null,
      memberLevelExpanded: false
    }
  },
  computed: {
    rangeOptions () {
      return RANGE_OPTIONS
    },
    selectedRange () {
      const now = new Date()
      if (this.activeRange === 'today') {
        return buildRange(startOfDay(now), endOfDay(now), '今日')
      }
      if (this.activeRange === 'yesterday') {
        const day = addDays(now, -1)
        return buildRange(startOfDay(day), endOfDay(day), '昨日')
      }
      if (this.activeRange === '7d') {
        return buildRange(startOfDay(addDays(now, -6)), endOfDay(now), '7天')
      }
      if (this.activeRange === '30d') {
        return buildRange(startOfDay(addDays(now, -29)), endOfDay(now), '30天')
      }
      if (this.activeRange === 'month') {
        return buildRange(startOfDay(new Date(now.getFullYear(), now.getMonth(), 1)), endOfDay(now), '本月')
      }
      if (Array.isArray(this.customRange) && this.customRange.length === 2) {
        const start = startOfDay(parseDate(this.customRange[0]) || addDays(now, -6))
        const end = endOfDay(parseDate(this.customRange[1]) || now)
        const diff = diffDays(start, end)
        return buildRange(start, end, diff <= 1 ? '自定义' : `${diff}天`)
      }
      return buildRange(startOfDay(addDays(now, -6)), endOfDay(now), '7天')
    },
    currentRangeLabel () {
      return this.dashboard.rangeLabel || this.selectedRange.label
    },
    summary () {
      return this.dashboard.summary || buildEmptySummary()
    },
    orderTrendPayload () {
      return this.dashboard.orderTrend || buildEmptyTrend()
    },
    pointTrendPayload () {
      return this.dashboard.pointTrend || buildEmptyTrend()
    },
    orderChart () {
      return buildLineChart(this.orderTrendPayload.series || [])
    },
    pointChart () {
      return buildLineChart(this.pointTrendPayload.series || [])
    },
    orderTrendTitle () {
      return `兑换订单${this.orderTrendPayload.days || 7}天趋势图`
    },
    pointTrendTitle () {
      return `每日积分消耗${this.pointTrendPayload.days || 7}天趋势图`
    },
    orderTrendTotal () {
      return Number(this.orderTrendPayload.total || 0)
    },
    pointTrendTotal () {
      return Number(this.pointTrendPayload.total || 0)
    },
    orderTrendAverage () {
      return Number(this.orderTrendPayload.average || 0)
    },
    pointTrendAverage () {
      return Number(this.pointTrendPayload.average || 0)
    },
    categorySlices () {
      return (this.dashboard.categorySlices || []).map((item, index) => ({
        ...item,
        color: CATEGORY_COLORS[index % CATEGORY_COLORS.length]
      }))
    },
    categoryChartStyle () {
      if (!this.categorySlices.length) {
        return { background: 'conic-gradient(#dbeafe 0deg 360deg)' }
      }
      let offset = 0
      const stops = this.categorySlices.map(item => {
        const start = offset * 3.6
        offset += Number(item.percent || 0)
        return `${item.color} ${start}deg ${offset * 3.6}deg`
      })
      return { background: `conic-gradient(${stops.join(', ')})` }
    },
    topCategoryName () {
      return this.categorySlices.length ? this.categorySlices[0].name : '-'
    },
    topCategoryShare () {
      return this.categorySlices.length ? `${this.categorySlices[0].percent}% 热度占比` : '暂无数据'
    },
    memberLevelCards () {
      return (this.dashboard.memberLevelCards || []).map((item, index) => ({
        ...item,
        color: LEVEL_COLORS[index % LEVEL_COLORS.length]
      }))
    },
    visibleMemberLevelCards () {
      return this.memberLevelExpanded ? this.memberLevelCards : this.memberLevelCards.slice(0, 3)
    },
    hiddenMemberLevelCount () {
      return Math.max(this.memberLevelCards.length - 3, 0)
    },
    activityLogs () {
      return this.dashboard.activityLogs || []
    },
    scrollingActivityLogs () {
      if (this.activityLogs.length <= 1) {
        return this.activityLogs
      }
      const duplicated = this.activityLogs.map((item, index) => ({ ...item, key: `${item.key}-dup-${index}` }))
      return this.activityLogs.concat(duplicated)
    },
    activityTrackStyle () {
      return {
        transform: `translateY(-${this.activityIndex * ACTIVITY_ROW_HEIGHT}px)`,
        transition: this.activityTransitionEnabled ? 'transform 0.45s ease' : 'none'
      }
    },
    warningCards () {
      return this.dashboard.warningCards || []
    },
    scrollingWarningCards () {
      if (this.warningCards.length <= 1) {
        return this.warningCards
      }
      const duplicated = this.warningCards.map((item, index) => ({ ...item, key: `${item.key}-dup-${index}` }))
      return this.warningCards.concat(duplicated)
    },
    warningTrackStyle () {
      return {
        transform: `translateY(-${this.warningIndex * WARNING_ROW_HEIGHT}px)`,
        transition: this.warningTransitionEnabled ? 'transform 0.45s ease' : 'none'
      }
    },
    alertCount () {
      return this.warningCards.length
    },
    alertHeadline () {
      return this.warningCards.length ? this.warningCards[0].title : '运行平稳'
    },
    currentAdminName () {
      return this.dashboard.currentAdminName || '系统管理员'
    },
    latestUpdateText () {
      return formatDateTime(this.dashboard.latestUpdateTime)
    },
    kpiCards () {
      return [
        {
          label: '累计注册用户',
          value: formatNumber(this.summary.totalMembers),
          badge: `截止 ${formatDate(this.selectedRange.end)}`,
          extra: `活跃账户 ${formatNumber(this.summary.activeMembers)} 人`,
          tone: 'blue'
        },
        {
          label: `${this.selectedRange.shortLabel}兑换订单`,
          value: formatNumber(this.summary.totalOrders),
          badge: '订单监控',
          extra: `待推进 ${formatNumber(this.summary.pendingOrders)} / 已完成 ${formatNumber(this.summary.completedOrders)}`,
          tone: 'cyan'
        },
        {
          label: `${this.selectedRange.shortLabel}消耗积分`,
          value: formatNumber(this.summary.totalConsumedPoints),
          badge: '积分消耗',
          extra: `发放 ${formatNumber(this.summary.totalGrantedPoints)} / 净变化 ${signedNumber(this.summary.netPoints)}`,
          tone: 'orange'
        },
        {
          label: '库存预警商品',
          value: formatNumber(this.summary.lowStockGoods),
          badge: '库存风险',
          extra: this.summary.lowStockGoods > 0 ? '建议优先处理低库存热门商品' : '当前库存状态稳定',
          tone: 'rose'
        }
      ]
    },
    quickActions () {
      return [
        { key: 'export', label: '导出Excel报表', primary: true },
        { key: 'stock', label: '库存预警一键看', primary: false },
        { key: 'level', label: '会员等级管理', primary: false },
        { key: 'task', label: '积分任务状态', primary: false }
      ]
    }
  },
  created () {
    const now = new Date()
    this.customRange = [formatDate(addDays(now, -6)), formatDate(now)]
    this.loadDashboard()
  },
  beforeDestroy () {
    this.pauseAutoScroll()
    this.pauseWarningAutoScroll()
  },
  methods: {
    async loadDashboard () {
      this.loading = true
      try {
        const { data } = await http.get('/mall/dashboard/overview', {
          params: {
            startDate: formatDate(this.selectedRange.start),
            endDate: formatDate(this.selectedRange.end)
          }
        })
        this.dashboard = data.data || buildEmptyDashboard()
      } finally {
        this.loading = false
        this.resetAutoScroll()
      }
    },
    changeRange (key) {
      this.activeRange = key
      if (key === 'custom') {
        if (!Array.isArray(this.customRange) || this.customRange.length !== 2) {
          const now = new Date()
          this.customRange = [formatDate(addDays(now, -6)), formatDate(now)]
        }
        return
      }
      this.loadDashboard()
    },
    handleCustomRangeChange (value) {
      if (Array.isArray(value) && value.length === 2) {
        this.customRange = value
        this.loadDashboard()
      }
    },
    handleQuickAction (action) {
      if (action === 'export') {
        this.exportReport()
        return
      }
      if (action === 'stock') {
        this.scrollToSection('warning-panel')
        return
      }
      if (action === 'level') {
        this.goRoute('/mall/member-level')
        return
      }
      if (action === 'task') {
        this.goRoute('/mall/point-task')
      }
    },
    handleWarningAction (action) {
      const routeMap = {
        goods: '/mall/goods',
        order: '/mall/order',
        'point-task': '/mall/point-task',
        'operation-log': '/system/operation-log'
      }
      if (routeMap[action]) {
        this.goRoute(routeMap[action])
      }
    },
    exportReport () {
      const rows = [
        ['指标', '数值'],
        ['累计注册用户', this.summary.totalMembers],
        [`${this.selectedRange.shortLabel}兑换订单`, this.summary.totalOrders],
        [`${this.selectedRange.shortLabel}消耗积分`, this.summary.totalConsumedPoints],
        ['库存预警商品', this.summary.lowStockGoods],
        ['待推进订单', this.summary.pendingOrders],
        ['售后/退款订单', this.summary.refundOrders],
        ['净积分变化', this.summary.netPoints]
      ]
      const tableRows = rows.map(item => `<tr><td>${item[0]}</td><td>${item[1]}</td></tr>`).join('')
      const html = `\ufeff<html><head><meta charset="utf-8" /></head><body><table border="1">${tableRows}</table></body></html>`
      const blob = new Blob([html], { type: 'application/vnd.ms-excel;charset=utf-8;' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `驾驶舱报表-${formatDate(this.selectedRange.end)}.xls`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      this.$message.success('驾驶舱报表已导出')
    },
    goRoute (path) {
      if (!this.checkRoutePermission(path)) {
        return
      }
      if (path && path !== this.$route.path) {
        this.$router.push(path)
      }
    },
    checkRoutePermission (path) {
      const permission = ROUTE_PERMISSION_MAP[path]
      if (permission && !this.$hasPermission(permission)) {
        this.$message.warning('暂无当前功能权限')
        return false
      }
      return true
    },
    scrollToSection (id) {
      const target = document.getElementById(id)
      if (target && typeof target.scrollIntoView === 'function') {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    },
    resetAutoScroll () {
      this.pauseAutoScroll()
      this.pauseWarningAutoScroll()
      this.activityIndex = 0
      this.warningIndex = 0
      this.activityTransitionEnabled = false
      this.warningTransitionEnabled = false
      this.$nextTick(() => {
        this.activityTransitionEnabled = true
        this.warningTransitionEnabled = true
        this.resumeAutoScroll()
        this.resumeWarningAutoScroll()
      })
    },
    pauseAutoScroll () {
      if (this.activityTimer) {
        window.clearInterval(this.activityTimer)
        this.activityTimer = null
      }
    },
    resumeAutoScroll () {
      if (this.activityLogs.length <= 1 || this.activityTimer) {
        return
      }
      this.activityTimer = window.setInterval(() => {
        this.activityTransitionEnabled = true
        this.activityIndex += 1
        if (this.activityIndex >= this.activityLogs.length) {
          window.setTimeout(() => {
            this.activityTransitionEnabled = false
            this.activityIndex = 0
          }, 460)
        }
      }, 2400)
    },
    pauseWarningAutoScroll () {
      if (this.warningTimer) {
        window.clearInterval(this.warningTimer)
        this.warningTimer = null
      }
    },
    resumeWarningAutoScroll () {
      if (this.warningCards.length <= 1 || this.warningTimer) {
        return
      }
      this.warningTimer = window.setInterval(() => {
        this.warningTransitionEnabled = true
        this.warningIndex += 1
        if (this.warningIndex >= this.warningCards.length) {
          window.setTimeout(() => {
            this.warningTransitionEnabled = false
            this.warningIndex = 0
          }, 460)
        }
      }, 2600)
    },
    formatNumber,
    signedNumber,
    formatDiscount
  }
}

function buildLineChart (series) {
  const list = Array.isArray(series) ? series : []
  const width = 520
  const height = 220
  const paddingX = 26
  const top = 20
  const bottom = 28
  const maxValue = Math.max(...list.map(item => Number(item.value || 0)), 0)
  const safeMax = maxValue || 1
  const span = Math.max(list.length - 1, 1)
  const usableWidth = width - paddingX * 2
  const usableHeight = height - top - bottom
  const points = list.map((item, index) => {
    const x = paddingX + (usableWidth * index) / span
    const y = top + usableHeight - (Number(item.value || 0) / safeMax) * usableHeight
    return {
      key: item.key,
      label: item.label,
      value: Number(item.value || 0),
      x: round2(x),
      y: round2(y)
    }
  })

  const linePath = points.length
    ? points.map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`).join(' ')
    : ''
  const areaPath = points.length
    ? `${linePath} L ${points[points.length - 1].x} ${height - bottom} L ${points[0].x} ${height - bottom} Z`
    : ''

  return {
    maxValue,
    linePath,
    areaPath,
    points,
    ticks: points.filter((point, index) => {
      if (points.length <= 6) {
        return true
      }
      if (index === 0 || index === points.length - 1) {
        return true
      }
      return index % Math.ceil(points.length / 4) === 0
    })
  }
}

function buildRange (start, end, shortLabel) {
  return {
    start,
    end,
    shortLabel,
    label: `${formatDate(start)} 至 ${formatDate(end)}`
  }
}

function parseDate (value) {
  if (!value) {
    return null
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value
  }
  if (typeof value === 'number') {
    const date = new Date(value)
    return Number.isNaN(date.getTime()) ? null : date
  }
  const date = new Date(String(value).replace(/-/g, '/'))
  return Number.isNaN(date.getTime()) ? null : date
}

function startOfDay (date) {
  const next = new Date(date)
  next.setHours(0, 0, 0, 0)
  return next
}

function endOfDay (date) {
  const next = new Date(date)
  next.setHours(23, 59, 59, 999)
  return next
}

function addDays (date, offset) {
  const next = new Date(date)
  next.setDate(next.getDate() + offset)
  return next
}

function diffDays (start, end) {
  return Math.floor((endOfDay(end).getTime() - startOfDay(start).getTime()) / 86400000) + 1
}

function formatDate (value) {
  const date = parseDate(value)
  if (!date) {
    return '--'
  }
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`
}

function formatDateTime (value) {
  const date = parseDate(value)
  if (!date) {
    return '--'
  }
  return `${formatDate(date)} ${pad2(date.getHours())}:${pad2(date.getMinutes())}`
}

function pad2 (value) {
  return String(value).padStart(2, '0')
}

function formatNumber (value) {
  return Number(value || 0).toLocaleString('zh-CN')
}

function signedNumber (value) {
  const num = Number(value || 0)
  return `${num > 0 ? '+' : ''}${formatNumber(num)}`
}

function formatDiscount (discountRate) {
  const value = Number(discountRate || 10)
  return value <= 10 ? `${value} 折` : `${value}%`
}

function round2 (value) {
  return Math.round(value * 100) / 100
}
</script>

<style scoped>
.dashboard-page {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-bottom: 8px;
}

.topbar-panel {
  display: grid;
  grid-template-columns: minmax(0, 1.8fr) 320px;
  gap: 16px;
  padding: 20px;
  border-radius: 28px;
  background:
    radial-gradient(circle at top left, rgba(255, 255, 255, 0.18), transparent 28%),
    radial-gradient(circle at bottom right, rgba(104, 214, 255, 0.2), transparent 32%),
    linear-gradient(135deg, #0d47a1 0%, #0f5ec9 48%, #1b7df2 100%);
  color: #fff;
  box-shadow: 0 20px 44px rgba(21, 84, 182, 0.28);
}

.topbar-main {
  display: grid;
  grid-template-columns: minmax(220px, 1fr) minmax(260px, 1.15fr) minmax(220px, 1fr);
  gap: 16px;
}

.title-block,
.filter-block,
.quick-block,
.manager-card,
.notice-card {
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.12);
  padding: 18px 18px 16px;
  backdrop-filter: blur(8px);
}

.title-tag,
.block-label {
  display: inline-flex;
  align-items: center;
  height: 28px;
  padding: 0 12px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.14);
  font-size: 12px;
  letter-spacing: 1px;
}

.title-block h1 {
  margin: 14px 0 8px;
  font-size: 34px;
  line-height: 1.18;
}

.title-block p,
.range-caption,
.manager-card span,
.notice-line span {
  margin: 0;
  color: rgba(255, 255, 255, 0.82);
  line-height: 1.7;
}

.range-list,
.notice-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 14px;
}

.range-button {
  border: 0;
  border-radius: 999px;
  padding: 8px 14px;
  background: rgba(255, 255, 255, 0.14);
  color: #fff;
  cursor: pointer;
  transition: all 0.2s ease;
}

.range-button.active,
.range-button:hover {
  background: #fff;
  color: #0f5ec9;
}

.custom-picker {
  width: 100%;
  margin-top: 14px;
}

.quick-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-top: 14px;
}

.quick-action-button {
  height: 42px;
  border: 0;
  border-radius: 999px;
  background: #ffffff;
  color: #2453a6;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
  box-shadow: 0 8px 18px rgba(17, 57, 123, 0.16);
}

.quick-action-button.primary {
  background: linear-gradient(135deg, #6ec8ff 0%, #4fa0ff 100%);
  color: #ffffff;
}

.quick-action-button:hover {
  transform: translateY(-1px);
  box-shadow: 0 12px 22px rgba(17, 57, 123, 0.2);
}

.topbar-side {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.manager-title {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.78);
}

.manager-card strong,
.notice-head em {
  display: block;
  margin-top: 8px;
  font-size: 24px;
  font-style: normal;
}

.notice-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.notice-line {
  margin-top: 14px;
}

.notice-line strong {
  display: block;
  margin-top: 6px;
  font-size: 16px;
}

.kpi-row {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
}

.kpi-card,
.panel-card {
  background: #fff;
  border-radius: 24px;
  box-shadow: 0 14px 36px rgba(15, 38, 66, 0.08);
}

.kpi-card {
  padding: 18px 20px;
  border: 1px solid rgba(15, 38, 66, 0.05);
}

.kpi-card.blue { background: linear-gradient(180deg, #eff6ff 0%, #ffffff 100%); }
.kpi-card.cyan { background: linear-gradient(180deg, #ecfeff 0%, #ffffff 100%); }
.kpi-card.orange { background: linear-gradient(180deg, #fff7ed 0%, #ffffff 100%); }
.kpi-card.rose { background: linear-gradient(180deg, #fff1f2 0%, #ffffff 100%); }

.kpi-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  color: #526176;
  font-size: 13px;
}

.kpi-header small {
  color: #7c8ba1;
}

.kpi-value {
  margin: 16px 0 10px;
  font-size: 34px;
  font-weight: 700;
  color: #13293d;
}

.kpi-extra {
  color: #6c7a90;
  line-height: 1.6;
}

.grid-row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.panel-card {
  padding: 20px;
}

.panel-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
  margin-bottom: 16px;
}

.panel-head h3 {
  margin: 0;
  color: #14283c;
  font-size: 20px;
}

.panel-head p {
  margin: 8px 0 0;
  color: #7a879b;
  line-height: 1.7;
}

.panel-head strong {
  color: #0f5ec9;
  font-size: 24px;
  white-space: nowrap;
}

.chart-shell {
  position: relative;
  border-radius: 20px;
  background: linear-gradient(180deg, #f7fbff 0%, #ffffff 100%);
  border: 1px solid #edf2f7;
  padding: 14px 16px 12px;
}

.trend-svg {
  width: 100%;
  height: 220px;
}

.chart-axis {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  color: #8b98ab;
  font-size: 12px;
}

.panel-foot {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin-top: 14px;
  color: #66768c;
}

.analysis-content {
  display: grid;
  gap: 18px;
}

.category-content {
  grid-template-columns: 240px minmax(0, 1fr);
  align-items: center;
}

.donut-wrap {
  display: flex;
  justify-content: center;
}

.donut-chart {
  width: 210px;
  height: 210px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.donut-chart::after {
  content: '';
  position: absolute;
  inset: 32px;
  background: #ffffff;
  border-radius: 50%;
  box-shadow: inset 0 0 0 1px #edf2f7;
}

.donut-core {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  text-align: center;
}

.donut-core span,
.donut-core small,
.legend-main small,
.level-meta,
.activity-desc,
.warning-item p {
  color: #748397;
}

.donut-core strong {
  font-size: 22px;
  color: #14283c;
}

.legend-list,
.level-list,
.warning-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.legend-item,
.level-item,
.warning-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 18px;
  background: #f8fbff;
  border: 1px solid #edf2f7;
}

.legend-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  flex: 0 0 auto;
}

.legend-main {
  flex: 1;
  min-width: 0;
}

.legend-main strong,
.warning-title strong {
  color: #14283c;
}

.legend-item em {
  font-style: normal;
  color: #0f5ec9;
  font-weight: 600;
}

.level-item {
  display: block;
}

.level-top,
.level-meta,
.warning-title,
.activity-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.level-bar {
  margin: 12px 0 10px;
  width: 100%;
  height: 10px;
  border-radius: 999px;
  background: #eaf0f7;
  overflow: hidden;
}

.level-bar span {
  display: block;
  height: 100%;
  min-width: 10px;
  border-radius: inherit;
}

.level-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  height: 38px;
  border: 1px solid #dbe7f5;
  border-radius: 18px;
  background: #f7fbff;
  color: #0f5ec9;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.level-toggle:hover {
  background: #edf6ff;
  border-color: #bdd7f5;
}

.level-toggle i {
  font-style: normal;
  font-size: 16px;
  line-height: 1;
  transition: transform 0.2s ease;
}

.level-toggle i.expanded {
  transform: rotate(180deg);
}

.activity-marquee {
  height: 372px;
  overflow: hidden;
  position: relative;
}

.activity-track {
  display: flex;
  flex-direction: column;
}

.activity-item {
  height: 90px;
  display: grid;
  grid-template-columns: 132px minmax(0, 1fr) auto;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 18px;
  background: #f8fbff;
  border: 1px solid #edf2f7;
  margin-bottom: 12px;
}

.activity-time {
  color: #8b98ab;
  font-size: 13px;
}

.activity-content {
  min-width: 0;
}

.activity-title strong {
  color: #14283c;
}

.activity-desc {
  margin-top: 8px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.activity-side {
  color: #0f5ec9;
  font-weight: 700;
  white-space: nowrap;
}

.status-chip {
  display: inline-flex;
  align-items: center;
  height: 24px;
  padding: 0 10px;
  border-radius: 999px;
  font-size: 12px;
}

.status-chip.ok {
  background: #ecfdf3;
  color: #0d9b6b;
}

.status-chip.warning {
  background: #fff7ed;
  color: #ea580c;
}

.status-chip.danger {
  background: #fef2f2;
  color: #dc2626;
}

.status-chip.mute {
  background: #f1f5f9;
  color: #64748b;
}

.warning-item {
  align-items: flex-start;
  box-sizing: border-box;
  height: 104px;
}

.warning-main {
  flex: 1;
  min-width: 0;
}

.warning-main p {
  display: -webkit-box;
  margin: 8px 0 0;
  overflow: hidden;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.warning-item.warning {
  background: linear-gradient(180deg, #fffaf0 0%, #ffffff 100%);
}

.warning-item.danger {
  background: linear-gradient(180deg, #fff5f5 0%, #ffffff 100%);
}

.warning-title span {
  display: inline-flex;
  align-items: center;
  height: 24px;
  padding: 0 10px;
  border-radius: 999px;
  font-size: 12px;
  color: #0f5ec9;
  background: #eaf3ff;
}

.warning-marquee {
  height: 336px;
  overflow: hidden;
  position: relative;
}

.empty-state {
  padding: 24px 18px;
  border-radius: 18px;
  background: #f8fbff;
  border: 1px dashed #d6e1ee;
  color: #7b8ca2;
  text-align: center;
}

.activity-empty {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

@media (max-width: 1440px) {
  .topbar-panel {
    grid-template-columns: 1fr;
  }

  .topbar-side {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 1200px) {
  .topbar-main,
  .kpi-row,
  .grid-row,
  .category-content {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .dashboard-page {
    gap: 12px;
  }

  .topbar-panel,
  .panel-card {
    padding: 16px;
    border-radius: 20px;
  }

  .title-block h1 {
    font-size: 28px;
  }

  .topbar-side,
  .quick-grid {
    grid-template-columns: 1fr;
  }

  .panel-head,
  .activity-item,
  .warning-item {
    grid-template-columns: 1fr;
    flex-direction: column;
  }

  .activity-item {
    height: auto;
  }

  .activity-marquee {
    height: auto;
  }

  .warning-marquee,
  .warning-item {
    height: auto;
  }
}
</style>
